import React, { useContext, useEffect, useState } from 'react';
import { API_WWW } from 'src/services';
import {
  Questionnaire,
  QuestionnaireGroup,
  QuestionnaireQuestionAnswer,
  QuestionnaireResults,
  QuestionnaireResultsResult
} from 'src/@types';
import { useForm } from 'react-hook-form';
import { DynamicInput } from 'src/components/shared/form-elements';
import { Button, Box, VStack, useDisclosure, Heading } from '@chakra-ui/react';
import { FormContext } from 'src/context';
import { calculateResults } from './utils/questionnaire-utils';
import { getQueryParam, scrollTo } from 'src/utils';
import QuestionnaireResult from './questionnaire-result';
import { AppAlertDialog } from '../../shared/general';
import { FormattedMessage, IntlProvider } from 'react-intl';
import en from './translations/en';
import nl from './translations/nl';

interface QuestionnaireListProps {
  id?: string | null;
}

export const QuestionnaireList: React.FC<QuestionnaireListProps> = ({ id }) => {
  let lang = 'nl';
  if (window.location.pathname.includes('/en/')) {
    lang = 'en';
  }
  const paramLang = getQueryParam('lang');
  if (paramLang) {
    lang = paramLang;
  }
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireGroup>({} as QuestionnaireGroup);
  const [possibleResults, setPossibleResults] = useState<QuestionnaireResults>(
    {} as QuestionnaireResults
  );
  const [locale] = useState(lang);
  const [messages, setMessages] = useState({});
  const [result, setResult] = useState<QuestionnaireResultsResult>();
  const [answersGiven, setAnswersGiven] = useState<QuestionnaireQuestionAnswer[]>([]);
  const [activeStep, setActiveStep] = useState<1 | 2>(1);
  const { isLoading, isReady, hasError, setLoading, setIsReady, setError } =
    useContext(FormContext);

  const {
    register,
    handleSubmit,
    formState: { isValid }
  } = useForm({
    mode: 'onBlur'
  });

  useEffect(() => {
    fetchQuestionnaire().then((r) => r);
  }, []);

  useEffect(() => {
    setMessages(lang === 'en' ? en : nl);
  }, [locale]);

  const { isOpen, onToggle } = useDisclosure();

  const fetchQuestionnaire = async () => {
    //setError(false);
    try {
      const res = await API_WWW.get<Questionnaire>(`questionnaire/${id}`);
      setQuestionnaire(res.questions.groups[0]);
      setPossibleResults(res.results);
      setIsReady(true);
    } catch (e) {
      // Handle error
      setError(true);
    }
  };

  const onSubmit = (data: Record<string, string>) => {
    // Moved to a custom hook or utility function
    const [totalScore, answersGiven] = calculateResults(data, questionnaire);
    const result = possibleResults?.results.find(
      (r) => r.scoreMinValue <= totalScore && r.scoreMaxValue >= totalScore
    );

    setLoading(true);
    setResult(result);
    setAnswersGiven(answersGiven);

    setTimeout(() => {
      setLoading(false);
      setActiveStep(2);
      scrollTo('.react-tools');
    }, 1000);
    //console.log('Total Score: ', totalScore);

    window.sessionStorage.setItem('explanations', JSON.stringify(answersGiven));
    //console.log('Result: ', result);
  };

  return (
    <>
      <IntlProvider locale={locale} messages={messages}>
        {hasError && (
          <AppAlertDialog title={'let op!'} open={hasError} onClose={fetchQuestionnaire}>
            <Box>
              <FormattedMessage
                id={`questionnaire.list.error`}
                defaultMessage={`Door een technische storing kunnen we nu helaas geen berekening uitvoeren. Probeer het opnieuw.`}
              />
            </Box>
          </AppAlertDialog>
        )}
        {isReady && (
          <>
            <VStack display={activeStep === 1 ? undefined : 'none'} spacing={4} align="flex-start">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Heading as={`h3`} variant={`h3`} mb={4}>
                  {questionnaire?.title}
                </Heading>
                {questionnaire?.questions.map((question) => (
                  <Box pb={2} pt={2} key={question.id}>
                    <DynamicInput
                      id={question.id}
                      variant={`questionnaire`}
                      type={`radio`}
                      size={'wide'}
                      label={question.question}
                      validation={{ required: true }}
                      options={question.answers.map((a) => {
                        return { label: a.answer, value: a.id };
                      })}
                      register={register}
                    />
                  </Box>
                ))}
                <Button
                  isLoading={isLoading}
                  loadingText={
                    <FormattedMessage
                      id={`questionnaire.list.isSending`}
                      defaultMessage={`Bezig met versturen`}
                    />
                  }
                  type="submit"
                  mt={4}
                  colorScheme="orange"
                  isDisabled={!isValid}>
                  <FormattedMessage
                    id={'questionnaire.list.submit'}
                    defaultMessage={`Bekijk de uitslag`}
                  />
                </Button>
              </form>
            </VStack>
            <QuestionnaireResult
              result={result}
              answersGiven={answersGiven}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              isOpen={isOpen}
              onToggle={onToggle}
            />
          </>
        )}
      </IntlProvider>
    </>
  );
};

export default QuestionnaireList;
